import React from 'react'
import { useNavigate } from 'react-router-dom'
import useSWR, { useSWRConfig } from 'swr'

import { getClients, getPlanLimits } from '../../api'
import { useAuth } from '../../context/auth'
import Spinner from '../../components/ui/spinner'
import ZeroTotalSendModal from '../../components/invoices/zeroTotalSendModal'
import config from '../../config'
import { executeGuestHydrationPlan } from '../../lib/executeGuestHydration'
import {
  clearGuestInvoiceDraftStorage,
  loadGuestInvoiceDraft,
  persistHydrationFailureFallback,
} from '../../lib/guestInvoiceDraftStorage'
import {
  buildHydrationFailureFallback,
  GuestHydrationOutcome,
  applyExternalPostSaveRedirect,
  planGuestHydration,
  ProfileOverwriteChoice,
  resolvePostSaveRedirect,
  shouldConfirmZeroTotalSend,
} from '../../lib/guestHydrationOrchestrator'
import { revalidateClientCaches } from '../../lib/revalidateClientCaches'
import { revalidateInvoiceSummaryCaches } from '../../lib/revalidateInvoiceSummaryCaches'
import ProfileOverwritePrompt from './ProfileOverwritePrompt'

const GuestHydrationRunner = () => {
  const { user, mutate: mutateUser } = useAuth()
  const navigate = useNavigate()
  const { mutate: mutateCache } = useSWRConfig()
  const draft = React.useMemo(() => loadGuestInvoiceDraft(), [])
  const [overwriteChoice, setOverwriteChoice] = React.useState<ProfileOverwriteChoice | undefined>()
  const [awaitingProfileChoice, setAwaitingProfileChoice] = React.useState(false)
  const [zeroTotalConfirm, setZeroTotalConfirm] = React.useState<{
    invoiceId: string
    sendEmailPath: string
  } | null>(null)
  const executionStartedRef = React.useRef(false)

  const { data: clients, isLoading: isLoadingClients } = useSWR('/api/clients', getClients)
  const { data: planLimits, isLoading: isLoadingPlanLimits } = useSWR('/api/plan-limits', getPlanLimits)

  const prerequisitesFailed = (
    (!isLoadingClients && clients == null)
    || (!isLoadingPlanLimits && planLimits == null)
  )

  const hydrationOutcome = React.useMemo((): GuestHydrationOutcome | null => {
    if (!draft || isLoadingClients || isLoadingPlanLimits) return null
    if (clients == null || planLimits == null) return null

    return planGuestHydration({
      draft,
      profile: { businessDetails: user?.businessDetails },
      clients,
      planLimits: {
        invoiceCount: user?.invoiceCount ?? 0,
        maxAmountOfInvoices: planLimits.maxAmountOfInvoices ?? 0,
        hasActiveSubscription: !!user?.hasActiveSubscription,
        freeAccess: !!planLimits.freeAccess,
      },
      overwriteChoice,
      accountEmail: user?.email,
    })
  }, [
    draft,
    clients,
    planLimits,
    user?.businessDetails,
    user?.email,
    user?.invoiceCount,
    user?.hasActiveSubscription,
    isLoadingClients,
    isLoadingPlanLimits,
    overwriteChoice,
  ])

  const fallbackToNewInvoice = React.useCallback(() => {
    if (draft) {
      persistHydrationFailureFallback(buildHydrationFailureFallback(draft))
    }
    navigate('/invoices/new', { replace: true })
  }, [draft, navigate])

  React.useEffect(() => {
    if (prerequisitesFailed && !executionStartedRef.current) {
      executionStartedRef.current = true
      fallbackToNewInvoice()
      return
    }

    if (!hydrationOutcome || executionStartedRef.current) return

    if (hydrationOutcome.status === 'awaitProfileChoice') {
      setAwaitingProfileChoice(true)
      return
    }

    if (hydrationOutcome.status === 'planLimitBlocked') {
      executionStartedRef.current = true
      navigate(hydrationOutcome.redirectTo, { replace: true })
      return
    }

    if (hydrationOutcome.status === 'invalid') {
      executionStartedRef.current = true
      fallbackToNewInvoice()
      return
    }

    if (hydrationOutcome.status !== 'execute') return

    executionStartedRef.current = true
    setAwaitingProfileChoice(false)

    const run = async () => {
      const result = await executeGuestHydrationPlan(hydrationOutcome.plan)
      if (!result.ok) {
        fallbackToNewInvoice()
        return
      }

      clearGuestInvoiceDraftStorage()
      await Promise.all([
        mutateUser(),
        mutateCache('/api/invoices/next-code'),
        mutateCache('/api/invoices'),
        mutateCache(`/api/invoices/${result.invoiceId}`),
        revalidateInvoiceSummaryCaches(mutateCache),
        revalidateClientCaches(mutateCache),
      ])

      const redirect = resolvePostSaveRedirect(
        result.postSaveAction,
        config.REACT_APP_API_URL,
      )
      if (redirect.kind === 'spa') {
        if (draft && shouldConfirmZeroTotalSend(draft)) {
          setZeroTotalConfirm({
            invoiceId: result.invoiceId,
            sendEmailPath: redirect.path,
          })
          return
        }
        navigate(redirect.path, { replace: true })
        return
      }

      applyExternalPostSaveRedirect(
        redirect,
        (path) => navigate(path, { replace: true }),
        (url) => {
          window.open(url, '_blank')
        },
      )
    }

    void run()
  }, [draft, fallbackToNewInvoice, hydrationOutcome, mutateCache, mutateUser, navigate, prerequisitesFailed])

  const handleProfileChoice = (choice: ProfileOverwriteChoice) => {
    setAwaitingProfileChoice(false)
    executionStartedRef.current = false
    setOverwriteChoice(choice)
  }

  if (awaitingProfileChoice) {
    return <ProfileOverwritePrompt open onChoose={handleProfileChoice} />
  }

  return (
    <section className='container col-xxl-8 py-5 text-center' aria-live='polite'>
      <Spinner />
      <p className='text-muted mt-3 mb-0'>Saving your invoice…</p>
      <ZeroTotalSendModal
        open={zeroTotalConfirm != null}
        onClose={() => {
          if (!zeroTotalConfirm) return
          navigate(`/invoices/${zeroTotalConfirm.invoiceId}`, { replace: true })
        }}
        onConfirm={() => {
          if (!zeroTotalConfirm) return
          navigate(zeroTotalConfirm.sendEmailPath, { replace: true })
        }}
      />
    </section>
  )
}

export default GuestHydrationRunner
